import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlatformLayout } from "./platform-layout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Search,
  Shield,
  ShieldCheck,
  Headphones,
  Briefcase,
  Receipt,
  Megaphone,
  UserCircle,
  Mail,
  Clock,
  KeyRound,
} from "lucide-react";

type PlatformRole =
  | "super_admin"
  | "admin"
  | "support"
  | "sales"
  | "billing"
  | "marketing";

interface PlatformUserEntry {
  id: string;
  email: string;
  name: string;
  role: PlatformRole;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string | null;
  mfaEnabled: boolean;
}

interface PlatformUsersDirectory {
  groups: Record<PlatformRole, PlatformUserEntry[]>;
  total: number;
}

const ROLE_META: Record<
  PlatformRole,
  { label: string; icon: React.ElementType; tone: string }
> = {
  super_admin: { label: "Super Admins", icon: ShieldCheck, tone: "text-red-600" },
  admin: { label: "Admins", icon: Shield, tone: "text-blue-600" },
  support: { label: "Support", icon: Headphones, tone: "text-purple-600" },
  sales: { label: "Sales", icon: Briefcase, tone: "text-emerald-600" },
  billing: { label: "Billing", icon: Receipt, tone: "text-amber-600" },
  marketing: { label: "Marketing", icon: Megaphone, tone: "text-pink-600" },
};

const ROLE_ORDER: PlatformRole[] = [
  "super_admin",
  "admin",
  "support",
  "sales",
  "billing",
  "marketing",
];

function getInitials(name: string, email: string) {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email ? email.slice(0, 2).toUpperCase() : "??";
}

function formatRelative(iso: string | null) {
  if (!iso) return "Never";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diffMs = Date.now() - t;
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function ProfileCard({
  entry,
  onPick,
}: {
  entry: PlatformUserEntry;
  onPick: (e: PlatformUserEntry) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(entry)}
      className="text-left w-full"
      data-testid={`button-platform-profile-${entry.id}`}
    >
      <Card className="p-3 flex items-center gap-3 hover-elevate active-elevate-2 transition-colors">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarFallback className="bg-gray-800 text-white text-xs font-semibold">
            {getInitials(entry.name, entry.email)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div
              className="text-sm font-medium truncate"
              data-testid={`text-platform-profile-name-${entry.id}`}
            >
              {entry.name || entry.email}
            </div>
            {!entry.isActive && (
              <Badge variant="outline" className="text-[10px] py-0">
                Inactive
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {entry.email}
          </div>
        </div>
        <div className="text-[10px] text-muted-foreground shrink-0 hidden sm:block">
          {formatRelative(entry.lastLoginAt)}
        </div>
      </Card>
    </button>
  );
}

export default function PlatformProfilesPage() {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<PlatformUserEntry | null>(null);

  const { data, isLoading, isError } = useQuery<PlatformUsersDirectory>({
    queryKey: ["/api/platform/users"],
  });

  const filteredGroups = useMemo(() => {
    if (!data) return null;
    const q = search.trim().toLowerCase();
    const result: Record<PlatformRole, PlatformUserEntry[]> = {
      super_admin: [],
      admin: [],
      support: [],
      sales: [],
      billing: [],
      marketing: [],
    };
    for (const role of ROLE_ORDER) {
      const list = data.groups[role] || [];
      result[role] = q
        ? list.filter(
            (u) =>
              (u.name || "").toLowerCase().includes(q) ||
              (u.email || "").toLowerCase().includes(q),
          )
        : list;
    }
    return result;
  }, [data, search]);

  const totalShown = useMemo(() => {
    if (!filteredGroups) return 0;
    return ROLE_ORDER.reduce(
      (acc, r) => acc + (filteredGroups[r]?.length || 0),
      0,
    );
  }, [filteredGroups]);

  return (
    <PlatformLayout>
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1
              className="text-2xl font-semibold tracking-tight"
              data-testid="heading-platform-profiles"
            >
              Profiles
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Directory of platform admin users, grouped by role.
            </p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Search name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8"
              data-testid="input-platform-profile-search"
            />
          </div>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && (
          <Card className="p-6 text-sm text-destructive" data-testid="text-platform-profiles-error">
            Failed to load platform users.
          </Card>
        )}

        {!isLoading && !isError && filteredGroups && (
          <>
            {totalShown === 0 && (
              <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="text-platform-profiles-empty">
                No platform users match your search.
              </Card>
            )}
            {ROLE_ORDER.map((role) => {
              const entries = filteredGroups[role] || [];
              if (entries.length === 0) return null;
              const meta = ROLE_META[role];
              const Icon = meta.icon;
              return (
                <section key={role} data-testid={`section-platform-role-${role}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon className={`h-4 w-4 ${meta.tone}`} />
                    <h2 className="text-sm font-semibold">{meta.label}</h2>
                    <Badge variant="secondary" className="text-xs">
                      {entries.length}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {entries.map((entry) => (
                      <ProfileCard
                        key={entry.id}
                        entry={entry}
                        onPick={setSelected}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-platform-profile-detail">
          <DialogHeader>
            <DialogTitle>Platform user</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Avatar className="h-14 w-14">
                  <AvatarFallback className="bg-gray-800 text-white text-base font-semibold">
                    {getInitials(selected.name, selected.email)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="text-base font-semibold truncate">
                    {selected.name || selected.email}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {ROLE_META[selected.role]?.label || selected.role}
                  </div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" />
                  <span className="truncate" data-testid="text-platform-profile-detail-email">
                    {selected.email}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <UserCircle className="h-4 w-4" />
                  <span>{selected.isActive ? "Active" : "Inactive"}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <KeyRound className="h-4 w-4" />
                  <span>MFA {selected.mfaEnabled ? "enabled" : "disabled"}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>
                    Last login: {formatRelative(selected.lastLoginAt)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PlatformLayout>
  );
}
